import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import useUserPrivacySettings from '../../../hooks/useUserPrivacySettings';
import Loading from '../../layout/Loading';
import './SettingsPages.css';
import './PrivacySettings.css';

function PrivacySettings() {
  const { t } = useTranslation(['profile', 'shared']);
  const { 
    hideLastActivity, 
    disableActivityNotifications, 
    disableProfileFollowing, 
    isLoading,
    refetch 
  } = useUserPrivacySettings();

  // Lokalne stany dla optymistycznych aktualizacji UI
  const [localHideLastActivity, setLocalHideLastActivity] = useState(hideLastActivity);
  const [localDisableActivityNotifications, setLocalDisableActivityNotifications] = useState(disableActivityNotifications);
  const [localDisableProfileFollowing, setLocalDisableProfileFollowing] = useState(disableProfileFollowing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Synchronizuj lokalne stany z hookiem gdy się zmienią
  React.useEffect(() => {
    setLocalHideLastActivity(hideLastActivity);
    setLocalDisableActivityNotifications(disableActivityNotifications);
    setLocalDisableProfileFollowing(disableProfileFollowing);
  }, [hideLastActivity, disableActivityNotifications, disableProfileFollowing]);

  const updatePrivacySetting = async (field, value) => {
    try {
      setSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error(t('profile.settings.pages.privacy.errorNoUser'));
      }

      const { error: updateError } = await supabase
        .from('users')
        .update({ [field]: value })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      // Nie wywołujemy refetch() - optymistyczna aktualizacja już zaktualizowała stan
      // refetch() powodowałoby re-render i scrollowanie na górę
      
      // Wyślij wiadomość do innych okien przez BroadcastChannel
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel('privacy_settings_sync');
        channel.postMessage({ 
          type: 'privacy_updated', 
          userId: user.id,
          field,
          value 
        });
        channel.close();
      }
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      setError(err.message || t('profile.settings.pages.privacy.errorUpdate'));
      // Przywróć poprzednią wartość w przypadku błędu
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const handleHideLastActivityChange = async (checked) => {
    // Optymistyczna aktualizacja UI
    setLocalHideLastActivity(checked);
    // Zapisz do bazy
    await updatePrivacySetting('hide_last_activity', checked);
  };

  const handleDisableActivityNotificationsChange = async (checked) => {
    // Optymistyczna aktualizacja UI
    setLocalDisableActivityNotifications(checked);
    // Zapisz do bazy
    await updatePrivacySetting('disable_activity_notifications', checked);
  };

  const handleDisableProfileFollowingChange = async (checked) => {
    // Optymistyczna aktualizacja UI
    setLocalDisableProfileFollowing(checked);
    // Zapisz do bazy
    await updatePrivacySetting('disable_profile_following', checked);
  };

  if (isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page-content">
          <Loading size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-page-content">
        {error && (
          <div className="privacy-settings-error settings-inline-alert settings-inline-alert--error">
            {error}
          </div>
        )}

        {/* Widoczność */}
        <div className="privacy-settings-section">
          <h3 className="privacy-settings-section-title">
            {t('profile.settings.pages.privacy.visibility')}
          </h3>
          
          <div className="privacy-settings-item">
            <div className="privacy-settings-item-content">
              <div className="privacy-settings-item-label">
                {t('profile.settings.pages.privacy.hideLastActivity')}
              </div>
              <div className="privacy-settings-item-description">
                {t('profile.settings.pages.privacy.hideLastActivityDescription')}
              </div>
            </div>
            <label className="privacy-switch">
              <input
                type="checkbox"
                checked={localHideLastActivity}
                onChange={(e) => handleHideLastActivityChange(e.target.checked)}
                disabled={saving}
              />
              <span className="privacy-switch-slider"></span>
            </label>
          </div>
        </div>

        {/* Powiadomienia */}
        <div className="privacy-settings-section">
          <h3 className="privacy-settings-section-title">
            {t('profile.settings.pages.privacy.notifications')}
          </h3>
          
          <div className="privacy-settings-item">
            <div className="privacy-settings-item-content">
              <div className="privacy-settings-item-label">
                {t('profile.settings.pages.privacy.disableActivityNotifications')}
              </div>
              <div className="privacy-settings-item-description">
                {t('profile.settings.pages.privacy.disableActivityNotificationsDescription')}
              </div>
            </div>
            <label className="privacy-switch">
              <input
                type="checkbox"
                checked={localDisableActivityNotifications}
                onChange={(e) => handleDisableActivityNotificationsChange(e.target.checked)}
                disabled={saving}
              />
              <span className="privacy-switch-slider"></span>
            </label>
          </div>
        </div>

        {/* Profil */}
        <div className="privacy-settings-section">
          <h3 className="privacy-settings-section-title">
            {t('profile.settings.pages.privacy.profile')}
          </h3>
          
          <div className="privacy-settings-item">
            <div className="privacy-settings-item-content">
              <div className="privacy-settings-item-label">
                {t('profile.settings.pages.privacy.disableProfileFollowing')}
              </div>
              <div className="privacy-settings-item-description">
                {t('profile.settings.pages.privacy.disableProfileFollowingDescription')}
              </div>
            </div>
            <label className="privacy-switch">
              <input
                type="checkbox"
                checked={localDisableProfileFollowing}
                onChange={(e) => handleDisableProfileFollowingChange(e.target.checked)}
                disabled={saving}
              />
              <span className="privacy-switch-slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacySettings;
